import { useEffect, useState, useCallback } from "react";
import { useLocation } from "wouter";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { get } from "@/lib/api";
import { APP_EVENTS } from "@/lib/brand";
import { stripMarkdown } from "@/lib/markdown";
import {
  FileText,
  StickyNote,
  Quote,
  BookOpen,
  Tag,
  Loader2,
} from "lucide-react";

export interface SearchResults {
  documents: Array<{
    id: number;
    title: string;
    content?: string | null;
    updated_at: string;
  }>;
  notes: Array<{
    id: number;
    content: string;
    parent_type: string;
    parent_id: number | null;
  }>;
  quotes: Array<{
    id: number;
    content: string;
    author: string;
  }>;
  scripture: Array<{
    id: number;
    book: string;
    chapter: number;
    verse_start: number;
    verse_end: number;
    text?: string | null;
  }>;
  tags: Array<{
    id: number;
    name: string;
    color?: string | null;
  }>;
}

const emptyResults: SearchResults = {
  documents: [],
  notes: [],
  quotes: [],
  scripture: [],
  tags: [],
};

export function SearchPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults>(emptyResults);
  const [loading, setLoading] = useState(false);
  const [, navigate] = useLocation();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const openSearch = () => setOpen(true);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener(APP_EVENTS.openSearch, openSearch);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(APP_EVENTS.openSearch, openSearch);
    };
  }, []);

  const runSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      setResults(emptyResults);
      return;
    }
    setLoading(true);
    try {
      const data = await get<SearchResults>(
        `/search?q=${encodeURIComponent(trimmed)}`,
      );
      setResults(data);
    } catch (err) {
      console.error(err);
      setResults(emptyResults);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => runSearch(query), 200);
    return () => clearTimeout(t);
  }, [query, open, runSearch]);

  const go = (href: string) => {
    setOpen(false);
    setQuery("");
    navigate(href);
  };

  const hasAny =
    results.documents.length +
      results.notes.length +
      results.quotes.length +
      results.scripture.length +
      results.tags.length >
    0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search documents, notes, quotes, scripture, tags…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {loading && (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Searching…
          </div>
        )}
        {!loading && query.trim() && !hasAny && (
          <CommandEmpty>No results for “{query.trim()}”.</CommandEmpty>
        )}
        {!loading && !query.trim() && (
          <CommandEmpty>Type to search your workspace.</CommandEmpty>
        )}

        {results.documents.length > 0 && (
          <CommandGroup heading="Documents">
            {results.documents.map((doc) => (
              <CommandItem
                key={`doc-${doc.id}`}
                value={`doc ${doc.title} ${doc.content || ""}`}
                onSelect={() => go(`/documents/${doc.id}/edit`)}
              >
                <FileText className="mr-2 h-4 w-4" />
                <span className="truncate">{doc.title || "Untitled"}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.notes.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Notes">
              {results.notes.map((note) => (
                <CommandItem
                  key={`note-${note.id}`}
                  value={`note ${note.content}`}
                  onSelect={() => {
                    if (note.parent_type === "document" && note.parent_id) {
                      go(`/documents/${note.parent_id}/edit`);
                    } else {
                      go(`/search?q=${encodeURIComponent(query.trim())}`);
                    }
                  }}
                >
                  <StickyNote className="mr-2 h-4 w-4" />
                  <span className="truncate">{stripMarkdown(note.content)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.quotes.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Quotes">
              {results.quotes.map((quote) => (
                <CommandItem
                  key={`quote-${quote.id}`}
                  value={`quote ${quote.content} ${quote.author}`}
                  onSelect={() => go(`/quotes/${quote.id}`)}
                >
                  <Quote className="mr-2 h-4 w-4" />
                  <span className="truncate">
                    {quote.author}: {stripMarkdown(quote.content)}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.scripture.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Scripture">
              {results.scripture.map((ref) => (
                <CommandItem
                  key={`ref-${ref.id}`}
                  value={`scripture ${ref.book} ${ref.chapter} ${ref.text || ""}`}
                  onSelect={() => go(`/scripture-references/${ref.id}`)}
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  <span className="truncate">
                    {ref.book} {ref.chapter}:{ref.verse_start}
                    {ref.verse_end > ref.verse_start ? `-${ref.verse_end}` : ""}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {results.tags.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Tags">
              {results.tags.map((tag) => (
                <CommandItem
                  key={`tag-${tag.id}`}
                  value={`tag ${tag.name}`}
                  onSelect={() => go(`/tags/${tag.id}`)}
                >
                  <Tag className="mr-2 h-4 w-4" />
                  #{tag.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {query.trim() && (
          <>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                value={`open search page ${query}`}
                onSelect={() =>
                  go(`/search?q=${encodeURIComponent(query.trim())}`)
                }
              >
                View all results for “{query.trim()}”
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
